import { CancelFunc, emptyCancel, wrapDispatch } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { convertError, EditReturn, ListReturn, Status } from 'lib/rpc'
import { Collector, unaryListCollect } from 'lib/rpcts'
import {
    AddAppEntitlementOwnerRequest,
    ListAppEntitlementOwnersRequest,
    ListAppEntitlementOwnersResponse,
    RemoveAppEntitlementOwnerRequest,
} from 'pbts/c1/api/app/v1/app_entitlement_owner'
import { AppEntitlementOwnersClient } from 'pbts/c1/api/app/v1/app_entitlement_owner.client'
import { User } from 'pbts/c1/api/user/v1/user'
import { Dispatch } from 'react'

export enum AppEntitlementOwnersAction {
    list = 'AppEntitlementOwnersAction.list',
    edit = 'AppEntitlementOwnersAction.edit',
}

export type EntitlementOwnersListAction = {
    type: AppEntitlementOwnersAction.list
    payload: ListReturn<User>
}

export type EntitlementOwnersEditAction = {
    type: AppEntitlementOwnersAction.edit
    payload: EditReturn
}

export class AppEntitlementOwnerCollector extends Collector<User> {
    push(resp: ListAppEntitlementOwnersResponse): void {
        this.buf = this.buf.concat(resp.list)
    }
}

export type AppEntitlementOwnersActions = EntitlementOwnersListAction | EntitlementOwnersEditAction

export const listAppEntitlementOwners = (
    dispatch: Dispatch<AppEntitlementOwnersActions>,
    appId: string,
    entitlementId: string,
    client?: AppEntitlementOwnersClient,
    notifyClient?: NotifyClient,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const collector = new AppEntitlementOwnerCollector()
    return unaryListCollect<ListAppEntitlementOwnersRequest, ListAppEntitlementOwnersResponse>(
        ListAppEntitlementOwnersRequest.create({
            appId,
            entitlementId,
        }),
        client.list.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: AppEntitlementOwnersAction.list,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                        },
                    })
                    break
                default:
                    dispatch({
                        type: AppEntitlementOwnersAction.list,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const editAppEntitlementOwners = (
    dispatcher: Dispatch<AppEntitlementOwnersActions>,
    client: AppEntitlementOwnersClient | undefined,
    appId: string,
    entitlementId: string,
    toAdd: User[],
    toRemove: User[],
): (() => void) | undefined => {
    const [dispatch, cancel] = wrapDispatch(dispatcher)
    if (!client) {
        return
    }

    let cancelled = false
    dispatch({ type: AppEntitlementOwnersAction.edit, payload: { status: Status.LOADING } })

    const doEditAppEntitlementOwners = async (): Promise<void> => {
        try {
            for (let i = 0; i < toAdd.length && !cancelled; i++) {
                const req = AddAppEntitlementOwnerRequest.create({ appId, entitlementId, userId: toAdd[i].id })

                await client.add(req)
            }

            for (let i = 0; i < toRemove.length && !cancelled; i++) {
                const req = RemoveAppEntitlementOwnerRequest.create({
                    appId,
                    entitlementId,
                    userId: toRemove[i].id,
                })

                await client.remove(req)
            }
        } catch (err) {
            dispatch({
                type: AppEntitlementOwnersAction.edit,
                payload: { status: Status.ERROR, error: convertError(err) },
            })
            return
        }
        dispatch({ type: AppEntitlementOwnersAction.edit, payload: { status: Status.OK } })
    }

    doEditAppEntitlementOwners()

    return (): void => {
        cancel()
        cancelled = true
    }
}
